// HoldOrdersPage.js
import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchHoldOrders, updateOrderStatus, deleteOrder, fetchHoldOrdersTotals, fetchHoldOrdersSummary, sendBulkWhatsAppTemplate } from "../../api/OrderAPIs";
import { fetchDesignById } from "../../api/DesignAPIs";
import { fetchTypeById } from "../../api/TypeAPIs";
import { formatPhoneWithCaseStyle } from "../../utils/orderLineDisplay";
import { useAuth } from "../../utils/auth";
import { canViewOrders, canMutateOrders, isAdmin } from "../../utils/roles";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL_V2 } from "../../api/URL_Header";
import { downloadCustomFile } from "../../api/FileAPIs";
import ViewConversationButton from "../../Components/orders/ViewConversationButton";
import MergeOrdersModal from "./MergeOrdersModal";
import {
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Package,
  User,
  Phone,
  MapPin,
  Map,
  DollarSign,
  Hash,
  FileText,
  Copy,
  Download,
  Link as LinkIcon,
  Edit,
  Trash2,
  Archive,
  Clock,
  CheckCircle,
  AlertTriangle,
  ShoppingCart,
  MessageSquare,
  Truck,
  AlertCircle,
  Layers,
  CopyCheck,
  Filter,
  Save,
  Plus,
  Search,
  Loader,
  XCircle,
  RotateCcw,
  PauseCircle,
  Merge,
} from "lucide-react";

const SkeletonLoader = ({ type }) => {
  if (type === "table") {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded mb-4"></div>
        {[...Array(3)].map((_, index) => (
          <div key={index} className="flex gap-4 border-b py-3">
            <div className="w-12 h-12 bg-gray-200 rounded"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  } else if (type === "card") {
    return (
      <div className="animate-pulse bg-white/80 backdrop-blur-sm rounded-2xl p-6">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  } else if (type === "productsCard") {
    return (
      <div className="animate-pulse bg-white/80 backdrop-blur-sm rounded-2xl p-6">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="h-6 bg-gray-200 rounded w-3/4"></div>
      </div>
    );
  }
  return null;
};

const HoldOrdersPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const canAct = canMutateOrders(authState.role);
  const [loadedOrders, setLoadedOrders] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isOrdersLoading, setIsOrdersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [showQuickMenu, setShowQuickMenu] = useState(false);
  const [sendingQuickTemplateId, setSendingQuickTemplateId] = useState(null);
  // expanded state: Set of orderNumbers that are currently expanded
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [mergingOrder, setMergingOrder] = useState(null);

  const toggleExpand = (orderNumber) => {
    setExpandedOrders(prev => {
      const next = new Set(prev);
      if (next.has(orderNumber)) {
        next.delete(orderNumber);
      } else {
        next.add(orderNumber);
      }
      return next;
    });
  };

  const { data: totals, isLoading: isLoadingTotals, error: errorTotals } = useQuery({
    queryKey: ["holdOrdersTotals"],
    queryFn: fetchHoldOrdersTotals,
    enabled: isAdmin(authState.role),
    staleTime: Infinity,
    placeholderData: { totalProfit: 0, totalAmount: 0, totalProductsSold: 0 },
  });

  const { data: holdOrdersSummary, isLoading: isLoadingSummary, error: errorSummary } = useQuery({
    queryKey: ["holdOrdersSummary"],
    queryFn: fetchHoldOrdersSummary,
    enabled: isAdmin(authState.role),
    staleTime: Infinity,
    placeholderData: { count: 0, totalProfit: 0, totalAmount: 0 },
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    if (!canViewOrders(authState.role)) {
      navigate("/login");
      return;
    }
    loadInitialOrders();
  }, [authState, navigate]);

  const isLoading = isLoadingTotals || isLoadingSummary;
  const queryError = errorTotals || errorSummary;

  const handleDownload = async (url, filename = "design.jpg") => {
    try {
      await downloadCustomFile(url, filename);
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("فشل التحميل. تحقق من السيرفر.");
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text).then(() => {
        toast.success("تم النسخ بنجاح!");
      }).catch(() => {
        fallbackCopy(text);
      });
    } else {
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    textArea.style.left = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        toast.success("تم النسخ بنجاح!");
      } else {
        toast.error("فشل النسخ، حاول مرة أخرى.");
      }
    } catch (err) {
      toast.error("فشل النسخ، حاول النسخ يدويًا.");
      console.error("Copy failed:", err);
    }
    document.body.removeChild(textArea);
  };

  const enrichOrders = async (data) => {
    return await Promise.all(
      data.map(async (order) => {
        let enrichedItems = [];
        for (const detail of order.orderDetails) {
          const design = await fetchDesignById(detail.designId);
          const type = await fetchTypeById(detail.typeId);
          if (!design || !type) continue;
          const baseItem = {
            id: detail.designId,
            name: design.Name,
            price: detail.price,
            type: formatPhoneWithCaseStyle(type.name, detail),
            driveLink: design.code.startsWith("CUSTOM")
              ? `${API_BASE_URL_V2}${design.driveLink}`
              : design.driveLink || "",
            code: design.code || "None",
          };
          for (let i = 0; i < detail.quantity; i++) {
            enrichedItems.push({
              ...baseItem,
              quantityIndex: i + 1,
              totalQuantity: detail.quantity,
            });
          }
        }
        return {
          orderNumber: order.orderId,
          items: enrichedItems,
          total: order.totalAmount,
          profit: order.profit || 0,
          date: new Date(order.date).toISOString().split("T")[0],
          address: order.address?.fullAddress || "غير معروف",
          state: order.address?.state || "غير معروف",
          phoneNumber: order.phoneNumber || "غير معروف",
          phoneNumber2: order.phoneNumber2 || "غير معروف",
          name: order.name || "غير معروف",
          note: order.note || "",
          status: order.status || "غير معروف",
        };
      })
    );
  };

  const loadInitialOrders = async () => {
    setIsOrdersLoading(true);
    try {
      const data = await fetchHoldOrders(0, 10);
      if (data.length === 0 || data.length < 10) setHasMore(false);
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders(enrichedOrders);
      setCurrentPage(1);
    } catch (err) {
      console.error("Error loading initial orders:", err);
      setError("فشل في تحميل الطلبات. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل الطلبات!");
    } finally {
      setIsOrdersLoading(false);
    }
  };

  const loadMoreOrders = async () => {
    setIsLoadingMore(true);
    try {
      const data = await fetchHoldOrders(currentPage, 10);
      if (data.length === 0 || data.length < 10) setHasMore(false);
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders((prev) => [...prev, ...enrichedOrders]);
      setCurrentPage((prev) => prev + 1);
    } catch (err) {
      console.error("Error loading more orders:", err);
      setError("فشل في تحميل المزيد من الطلبات. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل المزيد من الطلبات!");
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleUpdateStatus = async (orderId, newStatus, order) => {
    if (!canAct) return;
    let reason = "no reason (normal order)";
    if (newStatus === "Old") {
      try {
        const confirmUpdate = window.confirm("هل تريد تحديث الطلب إلى قديم؟");
        if (!confirmUpdate) return;
        await updateOrderStatus(orderId, newStatus, reason);
        setLoadedOrders(prev => prev.filter(o => o.orderNumber !== orderId));
        toast.success("تم تحديث الطلب إلى قديم!");
      } catch (err) {
        console.error(err);
        toast.error("فشل في العملية: " + (err.message || "خطأ غير معروف"));
      }
      return;
    }
    if (newStatus === "Hold") {
      reason = window.prompt("سبب وضع الطلب على الانتظار:");
      if (reason === null) return;
      if (!reason.trim()) { toast.error("يجب إدخال سبب!"); return; }
    } else if (newStatus === "Changes") {
      reason = window.prompt("سبب التغيير:");
      if (reason === null) return;
      if (!reason.trim()) { toast.error("يجب إدخال سبب!"); return; }
    } else if (newStatus === "Spam") {
      reason = window.prompt("سبب التغيير:");
      if (reason === null) return;
      if (!reason.trim()) { toast.error("يجب إدخال سبب!"); return; }
    }
    const confirm = window.confirm(`تأكيد تحديث الحالة إلى "${newStatus}"؟`);
    if (!confirm) return;
    try {
      await updateOrderStatus(orderId, newStatus, reason);
      setLoadedOrders(prev => prev.filter(o => o.orderNumber !== orderId));
      toast.success(`تم تحديث الطلب إلى ${newStatus} بنجاح`);
    } catch (err) {
      toast.error("فشل التحديث: " + err.message);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!canAct) return;
    if (!canMutateOrders(authState.role)) return;
    const confirmDelete = window.confirm(`هل أنت متأكد من حذف الطلب رقم ${orderId}؟`);
    if (!confirmDelete) return;
    try {
      await deleteOrder(orderId);
      setLoadedOrders((prev) => prev.filter((order) => order.orderNumber !== orderId));
      toast.success(`تم حذف الطلب رقم ${orderId} بنجاح!`);
    } catch (err) {
      console.error("Error deleting order:", err);
      toast.error("فشل في حذف الطلب. يرجى المحاولة مرة أخرى.");
    }
  };

  const handleOrdersMerged = async (updatedOrder, otherOrderId) => {
    try {
      const [enriched] = await enrichOrders([updatedOrder]);
      setLoadedOrders((prev) =>
        prev
          .filter((o) => o.orderNumber !== otherOrderId)
          .map((o) => (o.orderNumber === enriched.orderNumber ? enriched : o))
      );
    } catch (err) {
      console.error(err);
      await loadInitialOrders();
    }
  };

  const handleCopyOrderInfo = (order) => {
    const phone = `${order.phoneNumber}${order.phoneNumber2 !== "غير معروف" ? "-" + order.phoneNumber2 : ""}`;
    const formattedState = `${order.state} (D)`;
    const text = `${order.name}\t${phone}\t\t${order.address}\t${formattedState}\t${order.total}\tCOD`;
    copyToClipboard(text);
  };

  const handleCopyItems = (order) => {
    const lines = order.items.map((item, index) => {
      const itemNum = `${index + 1}-${order.items.length}`;
      const type = item.type || "Unknown Type";
      const driveLink = item.driveLink || "No Link";
      return `${itemNum}\t\tPhone Cover (H)\t${type}\t\t1\t${driveLink}`;
    }).join('\n');
    copyToClipboard(lines);
  };

  const handleCopyAddressClarification = () => {
    const message = `أهلاً بحضرتك

العنوان المسجل غير واضح أو ناقص تفاصيل مهمة لتسليم الطلب بنجاح

برجاء توضيح العنوان بالتفصيل:
• اسم الشارع
• رقم العمارة / الشقة
• معالم قريبة (مثل: بجوار صيدلية، أمام مدرسة، إلخ)

شكرًا لتفهمك، هنبدأ تجهيز الطلب فور توضيح العنوان`;
    copyToClipboard(message);
  };

  const handleCopyBostaMessage = () => {
    const message = `
بنحب نبلغ حضرتك إن شركة الشحن هتتواصل مع حضرتك على الواتساب خلال وقت قصير لتأكيد العنوان واستلام الطلب ✅
تنويه مهم:
في رسالة التأكيد اللي هتوصلك ممكن تلاقي اسم شركة مختلفة (مثل TeePlus) 💼  

كمان عدد القطع ممكن يظهر 1 حتى لو كانت اكتر من قطعة واحدة 🧾
يُرجى الرد على رسالة شركة الشحن علشان يتم تأكيد الطلب ويتم بدء الشحن ✉️⚡  

شكرًا لاختيارك جرابتكم 🌷`;
    copyToClipboard(message);
  };

  const filteredOrders = loadedOrders.filter(order => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      order.orderNumber.toString().includes(term) ||
      order.name.toLowerCase().includes(term) ||
      order.phoneNumber.includes(term) ||
      order.phoneNumber2?.includes(term)
    );
  });



  // ------------------------------------
  const QUICK_MESSAGES = [
    { id: "merge", title: "دمج الطلبات", icon: "🔄", templateId: 83262 },
    { id: "refused-before", title: "استفسار عن رفض الاستلام", icon: "❌", templateId: 83261 },
    { id: "address-missing", title: "توضيح العنوان", icon: "📍", templateId: 83263 },
    { id: "custom-design", title: "توضيح التصميم", icon: "📷", templateId: 83260 },
    { id: "normal", title: "بدأ الشات (عشان مناخدش بان)", icon: "✉️", templateId: 83264 },
  ];

  const getWhatsAppUrl = (phone) => {
    if (!phone || phone === "غير معروف" || phone === "Unknown Phone") return null;
    const digits = phone.replace(/[^0-9]/g, "").replace(/^0/, "20");
    return digits ? `https://wa.me/${digits}` : null;
  };

  const openQuickMessageMenu = (phoneNumber) => {
    setSelectedPhone(phoneNumber);
    setShowQuickMenu(true);
  };

  const openWhatsAppChat = () => {
    const url = getWhatsAppUrl(selectedPhone);
    if (!url) {
      toast.error("رقم الهاتف غير صالح");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
    setShowQuickMenu(false);
    setSelectedPhone(null);
  };

  const sendQuickTemplate = async (item) => {
    if (!selectedPhone || sendingQuickTemplateId) return;

    const confirmed = window.confirm(
      `إرسال قالب BeOn (${item.templateId}) — ${item.title}؟\n\nالرقم: ${selectedPhone}`
    );
    if (!confirmed) return;

    setSendingQuickTemplateId(item.id);
    try {
      const data = await sendBulkWhatsAppTemplate(item.templateId, selectedPhone);
      if (data.processed > 0) {
        toast.success(`تم إرسال القالب (${item.templateId}) بنجاح`);
        setShowQuickMenu(false);
        setSelectedPhone(null);
      } else {
        const failed = data.results?.find((r) => !r.success);
        toast.warning(failed?.error || "فشل إرسال القالب");
      }
    } catch (err) {
      toast.error(err.message || "فشل إرسال القالب");
    } finally {
      setSendingQuickTemplateId(null);
    }
  };
  // -------------------------------------

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8" dir="rtl">
      <ToastContainer position="top-center" />

      {mergingOrder && (
        <MergeOrdersModal
          sourceOrder={mergingOrder}
          onClose={() => setMergingOrder(null)}
          onMerged={handleOrdersMerged}
        />
      )}
      
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
          <div className="flex items-center gap-3">
            <PauseCircle className="text-amber-600" size={32} />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
              الطلبات المعلقة
            </h1>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 bg-white/80 backdrop-blur-sm text-gray-700 px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200"
          >
            <ChevronRight size={20} />
            العودة للرئيسية
          </button>
        </div>

        {/* Stats Cards */}
        {isAdmin(authState.role) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 hover:shadow-2xl transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-amber-100 rounded-xl">
                  <Package className="text-amber-600" size={24} />
                </div>
                <h2 className="text-xl font-bold text-gray-800">ملخص الطلبات المعلقة</h2>
              </div>
              {isLoading ? (
                <SkeletonLoader type="card" />
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-amber-50 rounded-xl">
                    <span className="text-gray-600">عدد الطلبات</span>
                    <span className="text-2xl font-bold text-amber-600">{holdOrdersSummary.count}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded-xl">
                    <span className="text-gray-600">إجمالي المبلغ</span>
                    <span className="text-2xl font-bold text-green-600">{holdOrdersSummary.totalAmount} ج.م</span>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 hover:shadow-2xl transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-indigo-100 rounded-xl">
                  <Layers className="text-indigo-600" size={24} />
                </div>
                <h2 className="text-xl font-bold text-gray-800">إجمالي المنتجات</h2>
              </div>
              {isLoading ? (
                <SkeletonLoader type="productsCard" />
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-indigo-50 rounded-xl">
                    <span className="text-gray-600">عدد المنتجات</span>
                    <span className="text-2xl font-bold text-indigo-600">{totals.totalProductsSold} قطعه</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Quick Message Buttons */}
        {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-amber-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-amber-100 rounded-xl">
                  <MapPin className="text-amber-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-amber-900 text-lg">طلب توضيح العنوان</h3>
                  <p className="text-sm text-amber-700">إذا كان العنوان غير واضح</p>
                </div>
              </div>
              <button
                onClick={handleCopyAddressClarification}
                className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 flex items-center gap-2"
              >
                <Copy size={18} />
                نسخ الرسالة
              </button>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <Truck className="text-blue-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-blue-900 text-lg">رسالة بوسطة</h3>
                  <p className="text-sm text-blue-700">لشركة الشحن</p>
                </div>
              </div>
              <button
                onClick={handleCopyBostaMessage}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 flex items-center gap-2"
              >
                <Copy size={18} />
                نسخ الرسالة
              </button>
            </div>
          </div>
        </div> */}

        {/* Search and Filter Bar */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 mb-6 border border-white/30">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="بحث برقم الطلب أو اسم العميل أو رقم الهاتف..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-12 pl-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-amber-300 focus:border-amber-400 transition-all"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
            >
              <Filter size={20} />
              تصفية
            </button>
          </div>
        </div>

        {/* Orders */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 overflow-x-auto">
          {isOrdersLoading ? (
            <SkeletonLoader type="table" />
          ) : (
            <>
              {filteredOrders.length > 0 ? (
                <>
                  {/* ==================== MOBILE CARDS VIEW ==================== */}
                  <div className="flex flex-col gap-5 md:hidden">
                    {filteredOrders.map((order) => {
                      const isExpanded = expandedOrders.has(order.orderNumber);
                      return (
                        <div
                          key={order.orderNumber}
                          className="rounded-2xl border border-purple-100 shadow-md overflow-hidden bg-white"
                        >
                          {/* Note Banner */}
                          {order.note ? (
                            <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 flex items-start gap-2">
                              <MessageSquare size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                              <p style={{ fontFamily: "cairo black" }} className="text-[17px] text-amber-800 leading-snug">
                                {order.note}
                              </p>
                            </div>
                          ) : (
                            <div className="bg-gray-50 border-b border-gray-100 px-4 py-2 flex items-center gap-2">
                              <MessageSquare size={14} className="text-gray-300" />
                              <p className="text-sm text-gray-400 italic">لا توجد ملاحظات</p>
                            </div>
                          )}

                          <div className="p-4 space-y-4">
                            {/* Order Number + Date */}
                            <div className="flex items-center justify-between">
                              <span className="font-mono font-bold text-gray-400 text-lg">#{order.orderNumber}</span>
                              <span className="text-lg text-purple-600">{order.date}</span>
                            </div>

                            {/* Customer Name */}
                            <div className="flex items-center gap-2">
                              <User size={15} className="text-gray-500" />
                              <span className="font-semibold text-gray-800 text-lg">{order.name}</span>
                            </div>

                            {/* Phone Numbers */}
                            <div className="space-y-3">
                              <div className="space-y-1.5">
                                <ViewConversationButton
                                  order={order}
                                  phone={order.phoneNumber}
                                  templatePreset="hold"
                                  canSend={canAct}
                                  compact
                                  className="w-full"
                                />
                                <button
                                  onClick={() => openQuickMessageMenu(order.phoneNumber)}
                                  className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-3 py-1 text-green-700 active:bg-green-100 transition w-full text-right"
                                >
                                  <Phone size={20} className="text-green-600 flex-shrink-0" />
                                  <span className="text-xl font-bold tracking-wide" style={{ fontFamily: "monospace" }}>
                                    {order.phoneNumber}
                                  </span>
                                </button>
                              </div>
                              {order.phoneNumber2 && order.phoneNumber2 !== "غير معروف" && (
                                <div className="space-y-1.5">
                                  <ViewConversationButton
                                    order={order}
                                    phone={order.phoneNumber2}
                                    templatePreset="hold"
                                    canSend={canAct}
                                    compact
                                    className="w-full"
                                    label="عرض محادثة الرقم التاني"
                                  />
                                  <button
                                    onClick={() => openQuickMessageMenu(order.phoneNumber2)}
                                    className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-3 py-1 text-green-700 active:bg-green-100 transition w-full text-right"
                                  >
                                    <Phone size={20} className="text-green-600 flex-shrink-0" />
                                    <span className="text-xl font-bold tracking-wide" style={{ fontFamily: "monospace" }}>
                                      {order.phoneNumber2}
                                    </span>
                                  </button>
                                </div>
                              )}
                            </div>

                            {/* Address + State */}
                            <div className="bg-gray-50 rounded-xl p-3 space-y-1">
                              <div className="flex items-start gap-2">
                                <MapPin size={15} className="text-gray-500 mt-0.5 flex-shrink-0" />
                                <span style={{ fontFamily: "cairo black" }} className="text-[12px] text-gray-700 leading-snug">
                                  {order.address}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Map size={15} className="text-purple-500" />
                                <span className="text-lg font-bold text-purple-600">{order.state}</span>
                              </div>
                            </div>

                            {/* Action Buttons */}
                            {canAct ? (
                            <div className="grid grid-cols-3 gap-2">
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Changes")}
                                className="col-span-2 bg-purple-500 text-white py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
                              >
                                <RotateCcw size={14} />
                                تغييرات
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Old", order)}
                                className="col-span-1 bg-gray-600 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
                              >
                                <Archive size={13} />
                                قديم
                              </button>
                              <button
                                onClick={() => setMergingOrder(order)}
                                className="col-span-3 bg-teal-600 text-white py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
                              >
                                <Merge size={14} />
                                دمج
                              </button>
                            </div>
                            ) : (
                              <p className="text-center text-sm text-slate-500 bg-slate-50 rounded-xl py-2 border border-slate-200">عرض فقط</p>
                            )}

                            {/* ── Total row + collapse toggle ── */}
                            <button
                              onClick={() => toggleExpand(order.orderNumber)}
                              className="w-full flex items-center justify-between bg-green-50 rounded-xl px-3 py-2 border border-green-200 active:bg-green-100 transition"
                            >
                              <div className="flex items-center gap-2">
                                {isExpanded
                                  ? <ChevronUp size={20} className="text-green-600" />
                                  : <ChevronDown size={20} className="text-green-600" />
                                }
                                <span className="text-gray-500 text-sm">
                                  {isExpanded ? "إخفاء المنتجات" : "عرض المنتجات والحذف"}
                                </span>
                              </div>
                              <span className="text-xl font-bold text-green-600">{order.total} ج.م</span>
                            </button>

                            {/* ── Collapsible section ── */}
                            {isExpanded && (
                              <div className="space-y-3">
                                {/* Products */}
                                <div className="space-y-1">
                                  {order.items.map((item, index) => (
                                    <div
                                      key={`${item.id}-${index}`}
                                      className="bg-teal-50 rounded-lg p-1.5 border border-teal-100"
                                    >
                                      <div className="font-medium text-teal-700 text-xs leading-tight">{item.name}</div>
                                      <div style={{ fontFamily: "cairo black" }} className="text-[10px] text-gray-600 leading-tight">
                                        {item.type}
                                      </div>
                                      <div className="flex items-center gap-1 mt-1 flex-wrap">
                                        <span className={`text-[10px] px-1.5 py-[2px] rounded font-mono ${item.code.startsWith("CUSTOM") ? "bg-green-100" : "bg-gray-100"}`}>
                                          {item.code}
                                        </span>
                                        {item.driveLink && (
                                          <div className="flex items-center gap-1">
                                            <a
                                              href={item.driveLink}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-teal-600 hover:text-teal-800"
                                              title="فتح الرابط"
                                            >
                                              <LinkIcon size={12} />
                                            </a>
                                            {item.code.startsWith("CUSTOM") && (
                                              <button
                                                onClick={() => handleDownload(item.driveLink, `${order.orderNumber}-${item.id}.jpg`)}
                                                className="flex items-center gap-1 text-white px-2 py-[2px] rounded text-[10px]"
                                                style={{ backgroundColor: "#15d225" }}
                                              >
                                                <Download size={10} />
                                                Download
                                              </button>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                {/* Delete / Spam */}
                                {canAct && (
                                <div className="grid grid-cols-2 gap-2">
                                  <button
                                    onClick={() => handleDeleteOrder(order.orderNumber)}
                                    className="bg-red-500 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
                                  >
                                    <Trash2 size={13} />
                                    حذف
                                  </button>
                                  <button
                                    onClick={() => handleUpdateStatus(order.orderNumber, "Spam")}
                                    className="bg-red-500 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1 active:scale-95 transition"
                                  >
                                    <AlertTriangle size={13} />
                                    Spam
                                  </button>
                                </div>
                                )}
                              </div>
                            )}
                            {/* ── End collapsible ── */}

                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* ==================== END MOBILE CARDS ==================== */}

                  {/* Desktop Table */}
                  <table className="w-full min-w-[1200px] table-auto hidden md:table">
                    <thead>
                      <tr className="bg-gradient-to-r from-purple-50 to-pink-50 border-b-2 border-purple-200">
                        <th className="p-4 text-right font-bold text-purple-800">رقم الطلب</th>
                        <th className="p-4 text-right font-bold text-purple-800">العميل</th>
                        <th className="p-4 text-right font-bold text-purple-800">العنوان</th>
                        <th className="p-4 text-center font-bold text-purple-800">الإجمالي</th>
                        <th className="p-4 text-right font-bold text-purple-800">المنتجات + الرابط</th>
                        <th className="p-4 text-right font-bold text-purple-800">ملاحظات</th>
                        <th className="p-4 text-center font-bold text-purple-800">نسخ</th>
                        <th className="p-4 text-center font-bold text-purple-800">إجراءات</th>
                        <th className="p-4 text-center font-bold text-purple-800">حذف</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrders.map((order) => (
                        <tr key={order.orderNumber} className="border-b border-gray-200 hover:bg-white/60 transition-colors">
                          <td className="p-4">
                            <span className="font-mono font-bold text-purple-600">#{order.orderNumber}</span>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <User size={16} className="text-gray-500" />
                              <span className="font-medium">{order.name}</span>
                            </div>
                            <br />
                            <div className="space-y-2">
                              <div className="space-y-1">
                                <ViewConversationButton
                                  order={order}
                                  phone={order.phoneNumber}
                                  templatePreset="hold"
                                  canSend={canAct}
                                  compact
                                  className="w-full"
                                />
                                <button
                                  onClick={() => openQuickMessageMenu(order.phoneNumber)}
                                  className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-3 py-1 text-green-700 active:bg-green-100 transition w-full text-right"
                                >
                                  <Phone size={20} className="text-green-600 flex-shrink-0" />
                                  <span className="text-xl font-bold tracking-wide" style={{ fontFamily: "monospace" }}>
                                    {order.phoneNumber}
                                  </span>
                                </button>
                              </div>
                              {order.phoneNumber2 && order.phoneNumber2 !== "غير معروف" && (
                                <div className="space-y-1">
                                  <ViewConversationButton
                                    order={order}
                                    phone={order.phoneNumber2}
                                    templatePreset="hold"
                                    canSend={canAct}
                                    compact
                                    className="w-full"
                                    label="عرض محادثة الرقم التاني"
                                  />
                                  <button
                                    onClick={() => openQuickMessageMenu(order.phoneNumber2)}
                                    className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-3 py-1 text-green-700 active:bg-green-100 transition w-full text-right"
                                  >
                                    <Phone size={20} className="text-green-600 flex-shrink-0" />
                                    <span className="text-xl font-bold tracking-wide" style={{ fontFamily: "monospace" }}>
                                      {order.phoneNumber2}
                                    </span>
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="p-4 max-w-[200px]">
                            <div className="flex items-start gap-2">
                              <MapPin size={16} className="text-gray-500 mt-1 flex-shrink-0" />
                              <span style={{fontFamily: "cairo black"}} className="text-[13px] line-clamp-5">{order.address}</span>
                            </div>
                            <br />
                            <div className="flex items-center gap-2">
                              <Map size={16} className="text-gray-500" />
                              <span className="text-2xl text-purple-600">{order.state}</span>
                            </div>
                          </td>
                          <td className="p-4 text-center">
                            <span className="font-bold text-xl text-green-600">{order.total} ج.م</span>
                          </td>
                          <td className="p-4">
                            {order.items.map((item, index) => (
                              <div key={`${item.id}-${index}`} className="mb-3">
                                <div className="flex items-start gap-2">
                                  <div className="flex-1">
                                    <div className="font-medium text-teal-700">{item.name}</div>
                                    <div style={{fontFamily:"cairo black"}} className="text-[13px] text-black-600">{item.type}</div>
                                    <div className="flex items-center gap-2 mt-1">
                                      {item.code.startsWith("CUSTOM") && (
                                        <span className="text-xs bg-green-100 px-2 py-1 rounded font-mono">{item.code}</span>
                                      )}
                                      {!item.code.startsWith("CUSTOM") && (
                                        <span className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">{item.code}</span>
                                      )}
                                      {item.driveLink && (
                                        <div className="flex items-center gap-1">
                                          <a
                                            href={item.driveLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-teal-600 hover:text-teal-800 transition-transform hover:scale-110"
                                            title="فتح الرابط"
                                          >
                                            <LinkIcon size={16} />
                                          </a>
                                          {item.code.startsWith("CUSTOM") && (
                                            <button
                                              onClick={() => handleDownload(item.driveLink, `${order.orderNumber}-${item.id}.jpg`)}
                                              className="flex items-center gap-2 text-white hover:text-white transition-transform hover:scale-110 px-3 py-2"
                                              title="تحميل الصورة"
                                              style={{ backgroundColor: "#15d225", borderRadius: "4px" }}
                                            >
                                              <Download size={16} />
                                              <span className="text-sm">Download</span>
                                            </button>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                {index < order.items.length - 1 && <hr className="my-2 border-gray-200" />}
                              </div>
                            ))}
                          </td>
                          <td className="p-4 max-w-[150px]">
                            <p style={{fontFamily:"cairo black"}} className="text-[15px]">{order.note || "—"}</p>
                          </td>
                          <td className="p-4">
                            <div className="flex flex-col gap-2 min-w-[100px]">
                              <button
                                onClick={() => handleCopyOrderInfo(order)}
                                className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-6 py-4 rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-300 text-lg font-bold flex items-center justify-center gap-2"
                              >
                                <Copy size={14} />
                                العميل
                              </button>
                              <button
                                onClick={() => handleCopyItems(order)}
                                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-4 rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-300 text-lg font-bold flex items-center justify-center gap-2"
                              >
                                <Layers size={14} />
                                المنتجات
                              </button>
                            </div>
                          </td>
                          <td className="p-4">
                            {canAct ? (
                            <div className="flex flex-col gap-2 min-w-[100px]">
                              <button
                                onClick={() => setMergingOrder(order)}
                                className="bg-teal-600 text-white px-3 py-2 rounded-lg hover:bg-teal-700 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Merge size={14} />
                                  دمج
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Old", order)}
                                className="bg-gray-600 text-white px-3 py-2 rounded-lg hover:bg-gray-700 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Archive size={14} />
                                  قديم
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Changes")}
                                className="bg-purple-500 text-white px-3 py-2 rounded-lg hover:bg-purple-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <RotateCcw size={14} />
                                  تغييرات
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Under Purchase")}
                                className="bg-indigo-500 text-white px-3 py-2 rounded-lg hover:bg-indigo-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <ShoppingCart size={14} />
                                  تحت الشراء
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Confirmed")}
                                className="bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <CheckCircle size={14} />
                                  تأكيد
                                </div>
                              </button>
                            </div>
                            ) : (
                              <p className="text-center text-sm text-slate-500">عرض فقط</p>
                            )}
                          </td>
                          <td className="p-4">
                            {canAct ? (
                            <div className="flex flex-col gap-2 min-w-[80px]">
                              <button
                                onClick={() => handleDeleteOrder(order.orderNumber)}
                                className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Trash2 size={14} />
                                  حذف
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Spam")}
                                className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <AlertTriangle size={14} />
                                  Spam
                                </div>
                              </button>
                            </div>
                            ) : (
                              <p className="text-center text-sm text-slate-500">عرض فقط</p>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Load More Button */}
                  {hasMore && (
                    <div className="text-center mt-8">
                      <button
                        onClick={loadMoreOrders}
                        disabled={isLoadingMore}
                        className="bg-gradient-to-r from-amber-600 to-orange-600 text-white px-8 py-4 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
                      >
                        {isLoadingMore ? (
                          <>
                            <Loader className="animate-spin" size={20} />
                            جاري التحميل...
                          </>
                        ) : (
                          <>
                            <ChevronLeft size={20} />
                            تحميل المزيد
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16">
                  <PauseCircle className="mx-auto mb-4 text-gray-400" size={64} />
                  <p className="text-2xl text-gray-700 mb-2">لا توجد طلبات معلقة</p>
                  <p className="text-lg text-gray-500">سيتم عرض الطلبات المعلقة هنا</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {/* Quick Message Modal */}
      {showQuickMenu && selectedPhone && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">رقم الهاتف</p>
                  <p className="text-2xl font-bold tracking-wider">{selectedPhone}</p>
                </div>
                <button
                  onClick={() => { setShowQuickMenu(false); setSelectedPhone(null); }}
                  className="text-white hover:bg-white/20 p-2 rounded-xl transition"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Options */}
            <div className="p-3 max-h-[70vh] overflow-auto">
              <button
                onClick={openWhatsAppChat}
                disabled={!!sendingQuickTemplateId}
                className="w-full flex items-center gap-4 p-4 hover:bg-green-50 rounded-2xl text-right transition group disabled:opacity-50 disabled:cursor-not-allowed border border-green-200 mb-1"
              >
                <div className="text-3xl">💬</div>
                <div className="flex-1">
                  <p className="font-bold text-lg text-gray-800 group-hover:text-green-600 transition">
                    افتح الشات علي واتساب
                  </p>
                </div>
                <ChevronRight className="text-gray-400 group-hover:text-green-600 transition" />
              </button>

              {QUICK_MESSAGES.map((item) => (
                <button
                  key={item.id}
                  onClick={() => sendQuickTemplate(item)}
                  disabled={!!sendingQuickTemplateId}
                  className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 rounded-2xl text-right transition group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="text-3xl">{item.icon}</div>
                  <div className="flex-1">
                    <p className="font-bold text-lg text-gray-800 group-hover:text-green-600 transition">
                      {item.title}
                    </p>
                    {/* <p className="text-sm text-gray-500">قالب BeOn #{item.templateId}</p> */}
                  </div>
                  {sendingQuickTemplateId === item.id ? (
                    <Loader className="text-green-600 animate-spin" size={20} />
                  ) : (
                    <ChevronRight className="text-gray-400 group-hover:text-green-600 transition" />
                  )}
                </button>
              ))}
            </div>

            <div className="p-4 border-t text-center text-sm text-gray-500">
              يتم الإرسال مباشرة عبر BeOn بعد التأكيد
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HoldOrdersPage;